import type { MediaRow } from '@/routes/media/types';
import type { TranslatedValue } from '@/lib/forms/translatable';

/**
 * docs/09-BUILD-PLAN.md Phase 6 step 11 — menus, useful links and settings.
 *
 * Hand-written for the reason `lib/api/resource.ts` sets out: Scramble types a
 * translatable field as `{[key: string]: unknown}`.
 */

/* ── docs/05-ADMIN-DASHBOARD.md §5.6, the menu builder ───────────────── */

export interface MenuSummary {
  id: number;
  key: string;
  name: string;
  items_count: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface MenuTree extends MenuSummary {
  items: MenuItemRow[];
}

/**
 * §5.6's three link types are three columns in §8 and one choice in the UI.
 * `LinkKind` is that choice; the API stores whichever of the three it maps to.
 */
export const LINK_KINDS = ['record', 'route', 'external'] as const;

export type LinkKind = (typeof LINK_KINDS)[number];

/** `link_status` — §5.6's "live validation" flag. */
export type LinkStatus = 'ok' | 'unpublished' | 'missing';

export interface MenuItemRow {
  id: number;
  parent_id: number | null;
  label: TranslatedValue;
  description: TranslatedValue;
  /** A `Linkables` alias — `page`, `document` — never a class name. */
  linkable_type: string | null;
  linkable_id: number | null;
  linkable_label: TranslatedValue | null;
  route_name: string | null;
  url: string | null;
  link_status: LinkStatus;
  target: '_self' | '_blank';
  icon: string | null;
  is_visible: boolean;
  sort_order: number;
  children: MenuItemRow[];
  translations_available: string[];
  created_at: string | null;
  updated_at: string | null;
}

export interface MenuItemInput {
  label: TranslatedValue;
  description: TranslatedValue;
  parent_id: number | null;
  linkable_type: string | null;
  linkable_id: number | null;
  route_name: string | null;
  url: string | null;
  target: '_self' | '_blank';
  icon: string | null;
  is_visible: boolean;
  sort_order: number;
}

/** What `POST /admin/menus/:key/reorder` takes: the tree, flattened. */
export interface MenuPosition {
  id: number;
  parent_id: number | null;
  sort_order: number;
}

/**
 * Which admin resource holds each linkable kind, for §5.6's record picker.
 *
 * The aliases come from the API (`GET /admin/menus/linkables`) so the picker
 * and the validation cannot disagree about what is linkable; this is only the
 * second half — where to *search* for one — which is a fact about the admin's
 * own routes rather than about the API.
 */
export const LINKABLE_RESOURCES: Record<string, string> = {
  page: 'pages',
  news: 'news',
  faculty: 'faculties',
  program: 'programs',
  vocational_program: 'vocational-programs',
  staff: 'staff',
  document: 'documents',
  project: 'projects',
};

/* ── docs/03-DATA-MODEL.md §8's `useful_links` ───────────────────────── */

export const USEFUL_LINK_GROUPS = ['home', 'vocational', 'partners'] as const;

export type UsefulLinkGroup = (typeof USEFUL_LINK_GROUPS)[number];

export interface UsefulLinkRow {
  id: number;
  group: UsefulLinkGroup;
  label: TranslatedValue;
  url: string;
  logo_media_id: number | null;
  logo: MediaRow | null;
  /*
   * `cover_url`, `photo_url` and `logo_url` were removed here.
   *
   * Task #71 moved images onto the media library and the API resources
   * went from a resolved URL string to the media object above. These
   * types kept declaring the old fields, so three list cells went on
   * reading `undefined` and rendering their empty state — the news list
   * showed a placeholder against 676 articles that had a cover, and
   * nothing failed. Use `thumbUrl(row.cover)`.
   */
  target: '_self' | '_blank';
  is_visible: boolean;
  sort_order: number;
  translations_available: string[];
  created_at: string | null;
  updated_at: string | null;
  /** Always null: §8 gives this table no `deleted_at`. */
  deleted_at: null;
}

export interface UsefulLinkInput {
  group: UsefulLinkGroup;
  label: TranslatedValue;
  url: string;
  logo_media_id: number | null;
  target: '_self' | '_blank';
  is_visible: boolean;
  sort_order: number;
}

/* ── docs/03-DATA-MODEL.md §8's `settings` ───────────────────────────── */

/** §8's `type` column, which is what the screen draws the control from. */
export type SettingType = 'string' | 'text' | 'json' | 'boolean' | 'number' | 'media';

export interface SettingRow {
  id: number;
  key: string;
  group: string;
  /**
   * Whatever §8 stored: a string, a list of phone numbers, the structured
   * `working_hours` object, or a `{ka, en}` map when `is_translatable`.
   */
  value: unknown;
  type: SettingType;
  is_translatable: boolean;
  is_public: boolean;
  updated_at: string | null;
}

export interface SettingGroupSummary {
  group: string;
  settings: number;
}
